import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { calendarApi, setCalendarToken } from './calendarApi';
import CalendarStatsLine from './CalendarStatsLine';

/**
 * The way in.
 *
 * SIGNING IN IS NOT THE SAME AS HAVING ACCESS, and this form no longer behaves
 * as though it were. Every subscriber with the right password gets a session,
 * including a past_due one whose card failed and a cancelled one who wants to
 * come back. The server returns the access verdict alongside the token and the
 * parent decides where they land: the calendar, the account page, or the
 * resubscribe page. Refusing them here left the two states we most want people
 * to be able to fix as the two states they could not sign in to fix.
 *
 * The error text never says whether the email was the part that was wrong.
 * One message for both cases is what keeps this from being a way to find out
 * who subscribes.
 */
function CalendarLogin({ onSignedIn }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    setBusy(true);
    try {
      const data = await calendarApi('/login', {
        method: 'POST',
        body: JSON.stringify({ email, password })
      });
      setCalendarToken(data.token);
      // The whole transition is the parent's, for the reason CalendarSubscribe
      // hands its own over: CalendarApp owns the router, and where a subscriber
      // lands depends on the access verdict the server just returned. A
      // past_due one belongs on the account page and a cancelled one on the
      // resubscribe page, neither of which this form can navigate to on its own.
      await onSignedIn(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '12px',
    fontSize: '16px', // 16px or larger, or iOS zooms the page on focus.
    borderRadius: '6px',
    border: '1px solid var(--color-border)',
    background: 'var(--color-input-bg, var(--color-surface))',
    color: 'var(--color-text)',
    boxSizing: 'border-box'
  };

  return (
    <div style={{
      maxWidth: '380px',
      margin: '0 auto',
      padding: '48px 16px',
      minHeight: '100vh'
    }}>
      <h1 style={{ color: 'var(--color-heading)', fontSize: '1.5rem', marginBottom: '4px' }}>
        Auction Calendar
      </h1>
      <CalendarStatsLine style={{ marginBottom: '28px' }} />

      <form onSubmit={submit}>
        <label htmlFor="calendar-email" style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>
          Email
        </label>
        <input
          id="calendar-email"
          type="email"
          autoComplete="username"
          style={{ ...inputStyle, marginBottom: '16px' }}
          value={email}
          onChange={e => setEmail(e.target.value)}
          required
        />

        <label htmlFor="calendar-password" style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>
          Password
        </label>
        <input
          id="calendar-password"
          type="password"
          autoComplete="current-password"
          style={{ ...inputStyle, marginBottom: '20px' }}
          value={password}
          onChange={e => setPassword(e.target.value)}
          required
        />

        {error && (
          <p style={{ color: 'var(--color-danger-text, var(--color-text))', marginBottom: '16px' }}>
            {error}
          </p>
        )}

        <button
          type="submit"
          className="btn btn-primary"
          style={{ width: '100%', padding: '12px', fontSize: '1rem' }}
          disabled={busy}
        >
          {busy ? 'Signing in' : 'Sign in'}
        </button>
      </form>

      <p style={{ marginTop: '20px', fontSize: '0.9rem' }}>
        <Link to="/forgot-password" style={{ color: 'var(--color-primary)' }}>
          Forgot your password?
        </Link>
      </p>

      <p style={{
        borderTop: '1px solid var(--color-border)',
        paddingTop: '20px',
        marginTop: '24px',
        color: 'var(--color-text-light)',
        fontSize: '0.9rem',
        lineHeight: 1.6
      }}>
        Not a subscriber yet? The Auction Calendar is $24 per month or $199 per year.
        <br />
        <Link to="/subscribe" style={{ color: 'var(--color-primary)', fontWeight: 700 }}>
          Subscribe now
        </Link>
      </p>

      <p style={{ color: 'var(--color-text-muted)', fontSize: '0.8rem', marginTop: '20px' }}>
        <Link to="/terms" style={{ color: 'var(--color-text-muted)' }}>Terms</Link>
        <span style={{ padding: '0 8px' }}>·</span>
        <Link to="/privacy" style={{ color: 'var(--color-text-muted)' }}>Privacy</Link>
      </p>
    </div>
  );
}

export default CalendarLogin;
